import { store } from '../../state/store';
import { sanitizeHtml } from '../../pipeline/sanitize';
import { annotateArticles, type ArticleAnchor } from '../../pipeline/extract';
import { loadNorma, refetchCurrent } from './load';
import { relativeTime } from '../../utils/hash';
import { api, BASE } from '../../api/client';
import type { Norma, SearchHit } from '../../api/types';
import { normalizeDocumentStructure } from '../../utils/text';

let articleEl: HTMLElement;
let currentAnchors: ArticleAnchor[] = [];
let currentSanitized = '';
let artSearchWrap: HTMLElement;
let artSearchInput: HTMLInputElement;
let substatsIntervalId: ReturnType<typeof setInterval> | null = null;
let vinculosWrap: HTMLElement;
let vinculosList: HTMLElement;
let activeVModo: 1 | 2 | null = null;
let navPanel: HTMLElement;
let navList: HTMLElement;
let docContextEl: HTMLElement;
let findInputEl: HTMLInputElement;
let findCountEl: HTMLElement;
let findHits: HTMLElement[] = [];
let findHitIndex = -1;
let vinculosItems: SearchHit[] = [];
let vinculosTipoFilter = '';
let globalListenersAttached = false;
let findTimeout: ReturnType<typeof setTimeout> | null = null;
let artSearchTimeout: ReturnType<typeof setTimeout> | null = null;

let aiPanel: HTMLElement;
let aiChatHistory: HTMLElement;
let aiInput: HTMLTextAreaElement;
let selectionPopover: HTMLElement;
let scrollProgressBar: HTMLElement;
let isDiffActive = false;
let cachedOriginalNorma: Norma | null = null;
let activeTooltip: HTMLElement | null = null;
let pendingRange: Range | null = null;
let rootEl: HTMLElement;

const NAV_WIDTH_KEY = 'viewer.navWidth.v1';
const NAV_MIN_WIDTH = 240;

const AI_WIDTH_KEY = 'viewer.aiWidth.v1';
const AI_MIN_WIDTH = 280;

export function renderViewerPanel(root: HTMLElement): void {
  rootEl = root;
  root.innerHTML = `
    <div class="viewer-toolbar" style="position: relative;">
      <!-- Barra de Progreso de Lectura -->
      <div class="scroll-progress" data-scroll-progress style="position: absolute; bottom: 0; left: 0; height: 3px; background: var(--primary); width: 0%; transition: width 0.1s ease-out; z-index: 10;"></div>
      
      <button class="icon-btn drawer-toggle md-state-layer" aria-label="Abrir búsqueda" title="Búsqueda">
        <i class="ph-bold ph-list"></i>
      </button>
      <div class="viewer-meta">
        <span class="viewer-title" data-title>LexCore</span>
        <span class="viewer-sub" data-sub></span>
      </div>
      <div class="viewer-actions">
        <span class="badge-estado" data-estado></span>
        <span data-cache-indicator></span>
        
        <button class="icon-btn md-state-layer" data-action="refresh" title="Actualizar desde la fuente oficial (Ctrl+Shift+R)">
          <i class="ph-bold ph-arrows-clockwise"></i>
        </button>
        
        <button class="btn ghost md-state-layer" data-action="texact" title="Abrir fuente original" hidden>
          <i class="ph ph-arrow-square-out"></i> Ver original
        </button>

        <!-- Comparar Reformas -->
        <button class="icon-btn md-state-layer" data-action="diff-toggle" title="Comparar con texto original (Ver reformas)" style="display: none;">
          <i class="ph-bold ph-git-diff"></i>
        </button>

        <!-- Asistente de IA -->
        <button class="icon-btn md-state-layer" data-action="ai-chat" title="Abrir Asistente IA (Mistral)">
          <i class="ph-bold ph-sparkle" style="color: var(--primary);"></i>
        </button>

        <!-- Menú Desplegable de Descargas (MD3) -->
        <div class="md-menu-wrap">
          <button class="btn filled md-state-layer" data-menu-trigger="export" title="Opciones de descarga">
            <i class="ph-bold ph-download-simple"></i> Descargar <i class="ph ph-caret-down"></i>
          </button>
          <div class="md-menu" data-menu="export">
            <button class="md-menu-item md-state-layer" data-action="pdf">
              <i class="ph-bold ph-file-pdf"></i> PDF <span class="kbd">Ctrl+P</span>
            </button>
            <button class="md-menu-item md-state-layer" data-action="docx">
              <i class="ph-bold ph-file-doc"></i> DOCX <span class="kbd">Ctrl+D</span>
            </button>
            <button class="md-menu-item md-state-layer" data-action="txt">
              <i class="ph-bold ph-file-txt"></i> TXT
            </button>
          </div>
        </div>

        <button class="icon-btn md-state-layer" data-action="copy-text" title="Copiar todo el texto">
          <i class="ph-bold ph-copy"></i>
        </button>
        <button class="icon-btn md-state-layer" data-action="copy" title="Copiar enlace">
          <i class="ph-bold ph-link"></i>
        </button>
      </div>
    </div>

    <div class="viewer-substatus" data-substatus></div>
    
    <div class="viewer-vinculos" data-vinculos hidden>
      <div class="vinculos-bar">
        <span class="vinculos-label">Relaciones</span>
        <button class="vinculos-btn md-state-layer" data-vbtn="1">
          <i class="ph ph-arrow-up-right"></i> Modifica <em data-vcount="1">—</em>
        </button>
        <button class="vinculos-btn md-state-layer" data-vbtn="2">
          <i class="ph ph-arrow-down-left"></i> Modificada por <em data-vcount="2">—</em>
        </button>
      </div>
      <div class="vinculos-list" data-vinculos-list hidden></div>
    </div>

    <div class="viewer-index-row">
      <div class="art-search-wrap hidden" data-art-search>
        <input class="art-search-input" type="search" placeholder="Ir a Art…" title="Buscar artículo o sección" autocomplete="off" />
      </div>
      <div class="doc-find-wrap" data-doc-find>
        <div class="search-input-icon-wrap">
          <i class="ph ph-magnifying-glass"></i>
          <input class="doc-find-input" type="search" placeholder="Buscar en texto…" autocomplete="off" />
        </div>
        <div class="find-controls">
          <button class="icon-btn sm md-state-layer" type="button" data-find-prev title="Anterior coincidencia">
            <i class="ph ph-caret-up"></i>
          </button>
          <button class="icon-btn sm md-state-layer" type="button" data-find-next title="Siguiente coincidencia">
            <i class="ph ph-caret-down"></i>
          </button>
          <span class="doc-find-count" data-find-count>0</span>
        </div>
      </div>
      <div class="viewer-zoom-controls">
        <button class="icon-btn md-state-layer" data-action="zoom-out" title="Reducir tamaño de texto">
          <i class="ph ph-minus"></i>
        </button>
        <button class="icon-btn md-state-layer" data-action="zoom-in" title="Aumentar tamaño de texto">
          <i class="ph ph-plus"></i>
        </button>
      </div>
      <button class="btn ghost md-state-layer" data-action="outline" title="Navegación del documento">
        <i class="ph-bold ph-book-open"></i> Índice
      </button>
      <div class="doc-context lx-scroll" data-doc-context></div>
    </div>

    <div class="viewer-body" data-viewer-body style="position: relative;">
      <article class="document lx-scroll" data-article>
        ${getWelcomeHtml()}
      </article>
      
      <!-- Panel de Navegación del Índice -->
      <aside class="viewer-nav hidden" data-nav-panel>
        <div class="viewer-nav-resizer" data-nav-resizer title="Arrastrar para ajustar ancho"></div>
        <div class="viewer-nav-head">
          <strong>Navegación</strong>
          <button class="icon-btn md-state-layer" type="button" data-nav-close title="Cerrar índice">
            <i class="ph ph-x"></i>
          </button>
        </div>
        <div class="viewer-nav-list lx-scroll" data-nav-list></div>
      </aside>

      <!-- Panel del Asistente IA (Mistral) -->
      <aside class="viewer-ai hidden" data-ai-panel>
        <div class="viewer-ai-resizer" data-ai-resizer title="Arrastrar para ajustar ancho"></div>
        <div class="viewer-ai-head">
          <strong><i class="ph-bold ph-sparkle" style="color: var(--primary);"></i> Asistente IA <span style="font-size: 10px; font-weight: 400; opacity: 0.65; margin-left: 4px; font-family: var(--mono);">(open-mistral-7b)</span></strong>
          <button class="icon-btn md-state-layer" type="button" data-ai-close title="Cerrar Asistente IA">
            <i class="ph ph-x"></i>
          </button>
        </div>
        
        <div class="viewer-ai-chat lx-scroll" data-ai-chat-history>
          <div class="ai-msg assistant">
            ¡Hola! Seleccioná cualquier fragmento de la ley para explicarlo en lenguaje claro, o haceme preguntas específicas usando la barra de abajo.
          </div>
        </div>

        <div class="viewer-ai-actions">
          <button class="btn outlined sm md-state-layer" data-ai-action="summary">
            <i class="ph ph-list-bullets"></i> Resumir
          </button>
          <button class="btn outlined sm md-state-layer" data-ai-action="obligations">
            <i class="ph ph-clock"></i> Plazos/Oblig.
          </button>
        </div>

        <div class="viewer-ai-input-wrap">
          <textarea class="viewer-ai-input" placeholder="Preguntar sobre esta ley..." rows="1"></textarea>
          <button class="icon-btn md-state-layer" data-ai-send title="Enviar pregunta"><i class="ph-bold ph-paper-plane-right"></i></button>
        </div>
      </aside>

      <!-- Botón Flotante para Volver Arriba -->
      <button class="back-to-top md-state-layer" data-back-to-top title="Volver arriba">
        <i class="ph-bold ph-arrow-up"></i>
      </button>
    </div>

    <!-- Popover contextual para selección de texto con IA y anotaciones -->
    <div class="selection-ai-popover hidden" data-selection-popover style="position: absolute; z-index: 100;">
      <div class="popover-row" style="display: flex; align-items: center; padding: 4px 8px; gap: 6px;">
        <button class="popover-btn" data-selection-ai-action="explain" title="Explicar con IA" style="background: var(--primary); color: var(--on-primary); border: none; cursor: pointer; display: flex; align-items: center; gap: 6px; font-size: 0.85em; font-weight: 600; padding: 5px 12px; border-radius: var(--radius-pill); box-shadow: var(--shadow-1); transition: transform 0.15s, filter 0.15s;">
          <i class="ph-bold ph-sparkle"></i> Explicar
        </button>
        <span class="popover-separator" style="width: 1px; height: 18px; background: var(--border-glass);"></span>
        <button class="hl-color-btn yellow" data-hl-color="yellow" title="Resaltar amarillo" style="width: 18px; height: 18px; border-radius: 50%; border: 1px solid rgba(0,0,0,0.15); cursor: pointer; background-color: #fef08a; padding: 0; transition: transform 0.15s;"></button>
        <button class="hl-color-btn green" data-hl-color="green" title="Resaltar verde" style="width: 18px; height: 18px; border-radius: 50%; border: 1px solid rgba(0,0,0,0.15); cursor: pointer; background-color: #bbf7d0; padding: 0; transition: transform 0.15s;"></button>
        <button class="hl-color-btn pink" data-hl-color="pink" title="Resaltar rosa" style="width: 18px; height: 18px; border-radius: 50%; border: 1px solid rgba(0,0,0,0.15); cursor: pointer; background-color: #fbcfe8; padding: 0; transition: transform 0.15s;"></button>
        <span class="popover-separator" style="width: 1px; height: 18px; background: var(--border-glass);"></span>
        <button class="popover-btn" data-selection-action="note" title="Agregar nota personal" style="background: transparent; border: none; color: inherit; cursor: pointer; display: flex; align-items: center; gap: 4px; font-size: 0.85em; font-weight: 500; padding: 4px 8px; border-radius: var(--radius-sm);">
          <i class="ph-bold ph-pencil-simple-line"></i> Nota
        </button>
      </div>
    </div>
    
    <div class="copy-toast" data-copy-toast>Enlace copiado ✓</div>
    <div class="copy-toast" data-copy-text-toast>Texto copiado ✓</div>
  `;

  articleEl = root.querySelector<HTMLElement>('[data-article]')!;
  artSearchWrap = root.querySelector<HTMLElement>('[data-art-search]')!;
  artSearchInput = artSearchWrap.querySelector<HTMLInputElement>('.art-search-input')!;
  vinculosWrap = root.querySelector<HTMLElement>('[data-vinculos]')!;
  vinculosList = root.querySelector<HTMLElement>('[data-vinculos-list]')!;
  navPanel = root.querySelector<HTMLElement>('[data-nav-panel]')!;
  navList = root.querySelector<HTMLElement>('[data-nav-list]')!;
  docContextEl = root.querySelector<HTMLElement>('[data-doc-context]')!;
  findInputEl = root.querySelector<HTMLInputElement>('[data-doc-find] .doc-find-input')!;
  findCountEl = root.querySelector<HTMLElement>('[data-find-count]')!;
  const viewerBody = root.querySelector<HTMLElement>('[data-viewer-body]')!;
  const navResizer = root.querySelector<HTMLElement>('[data-nav-resizer]')!;
  const titleEl = root.querySelector<HTMLElement>('[data-title]')!;
  const subEl = root.querySelector<HTMLElement>('[data-sub]')!;
  const estadoEl = root.querySelector<HTMLElement>('[data-estado]')!;
  const cacheInd = root.querySelector<HTMLElement>('[data-cache-indicator]')!;
  const subStatus = root.querySelector<HTMLElement>('[data-substatus]')!;
  const copyToast = root.querySelector<HTMLElement>('[data-copy-toast]')!;
  const copyTextToast = root.querySelector<HTMLElement>('[data-copy-text-toast]')!;
  const backToTopBtn = root.querySelector<HTMLButtonElement>('[data-back-to-top]')!;

  // '1' era el default que se grababa automáticamente (bug): aplastaba --font-doc con 1rem=15px.
  const storedZoom = localStorage.getItem('viewer.zoom.v1');
  if (storedZoom === '1') localStorage.removeItem('viewer.zoom.v1');
  let currentZoom = (storedZoom && storedZoom !== '1') ? parseFloat(storedZoom) : 0; // 0 = usar CSS

  const applyZoom = () => {
    if (currentZoom > 0) {
      articleEl.style.fontSize = `${currentZoom}rem`;
      localStorage.setItem('viewer.zoom.v1', currentZoom.toString());
    } else {
      articleEl.style.removeProperty('font-size');
      localStorage.removeItem('viewer.zoom.v1');
    }
  };
  applyZoom();

  const effectiveRem = (): number => {
    const px = parseFloat(getComputedStyle(articleEl).fontSize);
    const base = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return Math.round((px / base) * 10) / 10;
  };

  root.querySelector('[data-action="zoom-in"]')?.addEventListener('click', () => {
    if (currentZoom === 0) currentZoom = effectiveRem();
    if (currentZoom < 2.5) { currentZoom = Math.round((currentZoom + 0.1) * 10) / 10; applyZoom(); }
  });

  root.querySelector('[data-action="zoom-out"]')?.addEventListener('click', () => {
    if (currentZoom === 0) currentZoom = effectiveRem();
    if (currentZoom > 0.6) { currentZoom = Math.round((currentZoom - 0.1) * 10) / 10; applyZoom(); }
  });

  root.querySelector('.drawer-toggle')?.addEventListener('click', () => {
    store.drawerOpen.set(!store.drawerOpen.get());
  });

  const texactBtn = root.querySelector<HTMLButtonElement>('[data-action="texact"]')!;
  const diffToggleBtn = root.querySelector<HTMLButtonElement>('[data-action="diff-toggle"]')!;
  root.querySelector('[data-action="refresh"]')?.addEventListener('click', () => void refetchCurrent(true));
  texactBtn.addEventListener('click', () => openTexact());
  diffToggleBtn.addEventListener('click', () => void toggleDiff());

  root.querySelectorAll<HTMLButtonElement>('[data-vbtn]').forEach(btn => {
    btn.addEventListener('click', () => void handleVinculosClick(btn));
  });
  root.querySelector('[data-action="outline"]')?.addEventListener('click', () => {
    navPanel.classList.toggle('hidden');
    if (!navPanel.classList.contains('hidden')) {
      aiPanel.classList.add('hidden');
    }
  });
  root.querySelector('[data-nav-close]')?.addEventListener('click', () => {
    navPanel.classList.add('hidden');
  });
  // Mobile drawer: cerrar al tocar fuera (backdrop) o al presionar Escape
  viewerBody.addEventListener('click', (e) => {
    if (window.innerWidth > 1024) return;
    const target = e.target as Node;
    if (!navPanel.classList.contains('hidden') && !navPanel.contains(target)) {
      navPanel.classList.add('hidden');
    }
    if (!aiPanel.classList.contains('hidden') && !aiPanel.contains(target)) {
      aiPanel.classList.add('hidden');
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && window.innerWidth <= 1024) {
      navPanel.classList.add('hidden');
      aiPanel.classList.add('hidden');
    }
  });
  
  const savedNavWidth = loadNavWidth();
  if (savedNavWidth) {
    const max = Math.max(NAV_MIN_WIDTH + 80, Math.floor(viewerBody.clientWidth * 0.65));
    setNavWidth(clamp(savedNavWidth, NAV_MIN_WIDTH, max));
  }
  
  navResizer.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    navResizer.setPointerCapture(e.pointerId);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    
    const rect = viewerBody.getBoundingClientRect();
    const navRect = navPanel.getBoundingClientRect();
    const rightEdge = navRect.right;
    
    const onMove = (ev: PointerEvent): void => {
      const desired = rightEdge - ev.clientX;
      const max = Math.max(NAV_MIN_WIDTH + 80, Math.floor(rect.width * 0.65));
      const next = clamp(desired, NAV_MIN_WIDTH, max);
      setNavWidth(next);
      saveNavWidth(next);
    };
    const onUp = (ev: PointerEvent): void => {
      navResizer.releasePointerCapture(ev.pointerId);
      navResizer.removeEventListener('pointermove', onMove);
      navResizer.removeEventListener('pointerup', onUp);
      navResizer.removeEventListener('pointercancel', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    navResizer.addEventListener('pointermove', onMove);
    navResizer.addEventListener('pointerup', onUp);
    navResizer.addEventListener('pointercancel', onUp);
  });

  // Lógica de Menús MD3
  const menuTrigger = root.querySelector<HTMLButtonElement>('[data-menu-trigger="export"]')!;
  const exportMenu = root.querySelector<HTMLElement>('[data-menu="export"]')!;
  
  menuTrigger.addEventListener('click', (e) => {
    e.stopPropagation();
    exportMenu.classList.toggle('show');
  });

  // Cerrar menús al hacer clic fuera
  document.addEventListener('click', () => {
    exportMenu.classList.remove('show');
  });

  root.querySelector('[data-action="pdf"]')?.addEventListener('click', () => void doExportPdf());
  root.querySelector('[data-action="docx"]')?.addEventListener('click', () => void doExportDocx());
  root.querySelector('[data-action="txt"]')?.addEventListener('click', () => void doExportTxt());
  root.querySelector('[data-action="copy-text"]')?.addEventListener('click', () => void copyAllText(copyTextToast));
  root.querySelector('[data-action="copy"]')?.addEventListener('click', () => void copyLink(copyToast));

  backToTopBtn.addEventListener('click', () => {
    articleEl.scrollTo({ top: 0, behavior: 'smooth' });
  });

  artSearchInput.addEventListener('input', () => {
    if (artSearchTimeout) clearTimeout(artSearchTimeout);
    artSearchTimeout = setTimeout(() => {
      navigateToArt(artSearchInput.value);
    }, 300);
  });
  artSearchInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (artSearchTimeout) clearTimeout(artSearchTimeout);
      navigateToArt(artSearchInput.value, true);
    }
  });

  findInputEl.addEventListener('input', () => {
    if (findTimeout) clearTimeout(findTimeout);
    const val = findInputEl.value.trim();
    const isNum = /^\d/.test(val);
    
    findTimeout = setTimeout(() => {
      if (isNum) navigateToArt(val);
      applyFindHighlights(val, isNum);
    }, 300);
  });
  findInputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      focusFindHit(e.shiftKey ? -1 : 1);
    }
  });
  root.querySelector('[data-find-prev]')?.addEventListener('click', () => focusFindHit(-1));
  root.querySelector('[data-find-next]')?.addEventListener('click', () => focusFindHit(1));

  articleEl.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement | null)?.closest('a') as HTMLAnchorElement | null;
    if (!link) return;
    const href = link.getAttribute('href')?.trim();
    if (href && link.getAttribute('target') === '_blank') return;
    if (!href) return;
    if (/^(anexos\/|\/infolegInternet\/|infolegInternet\/)/i.test(href)) {
      e.preventDefault();
      const absolute = href.startsWith('/')
        ? new URL(href, 'https://servicios.infoleg.gob.ar').toString()
        : new URL(href, 'https://servicios.infoleg.gob.ar/infolegInternet/').toString();
      window.open(absolute, '_blank', 'noopener,noreferrer');
      return;
    }
    if (href.startsWith('#')) {
      e.preventDefault();
      const target = articleEl.querySelector<HTMLElement>(href);
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  let scrollTick = false;
  articleEl.addEventListener('scroll', () => {
    if (scrollTick) return;
    scrollTick = true;
    requestAnimationFrame(() => {
      scrollTick = false;
      updateDocContext();
      backToTopBtn.classList.toggle('visible', articleEl.scrollTop > 400);
      
      // Actualizar la barra de progreso de scroll
      if (scrollProgressBar) {
        const scrollHeight = articleEl.scrollHeight - articleEl.clientHeight;
        const progress = scrollHeight > 0 ? (articleEl.scrollTop / scrollHeight) * 100 : 0;
        scrollProgressBar.style.width = `${progress}%`;
      }
    });
  });

  store.currentNorma.subscribe((norma) => {
    if (!norma) {
      articleEl.innerHTML = getWelcomeHtml();
      titleEl.textContent = 'LexCore';
      subEl.textContent = '';
      estadoEl.hidden = true;
      cacheInd.innerHTML = '';
      texactBtn.hidden = true;
      resetVinculos();
      articleEl.scrollTop = 0;
      return;
    }
    try {
      const prefix = [norma.meta.tipo, norma.meta.numero ? `N\u00b0\u00a0${norma.meta.numero}` : ''].filter(Boolean).join('\u00a0');
      const descripcion = norma.meta.titulo ?? '';
      titleEl.textContent = prefix
        ? descripcion ? `${prefix}\u00a0\u00b7 ${descripcion}` : prefix
        : descripcion || 'Sin título';

      subEl.textContent = [
        norma.meta.sancion ? `Sanc. ${norma.meta.sancion}` : '',
        norma.meta.publicacion ? `Pub. ${norma.meta.publicacion}` : '',
      ].filter(Boolean).join(' \u00b7 ');

      const estadoLabel = labelForEstado(norma.meta.estado);
      estadoEl.hidden = !estadoLabel;
      if (estadoLabel) {
        estadoEl.dataset.estado = norma.meta.estado;
        estadoEl.textContent = estadoLabel;
      }

      if (norma.source === 'nacion') {
        texactBtn.hidden = false;
        texactBtn.textContent = 'Ver en Infoleg ↗';
      } else if (norma.source === 'bcra') {
        texactBtn.hidden = false;
        texactBtn.textContent = 'Abrir original ↗';
      } else {
        texactBtn.hidden = true;
      }

      // Si el documento tiene más de 15 segundos desde que se scrapeó, vino del caché global
      if (Date.now() - norma.fetchedAt > 15000) {
        cacheInd.innerHTML = '<span title="Cargada ultrarrápido desde la caché global" style="color: #eab308; margin-right: 4px; font-size: 1.1em; cursor: help; vertical-align: middle;">⚡</span>';
      } else {
        cacheInd.innerHTML = '';
      }

      paintNorma(norma);
      updateSubStatus(subStatus, norma);

      const mv = norma.meta.vinculosModifica;
      const mpv = norma.meta.vinculosModificadaPor;
      const showVinculos = norma.source === 'nacion' && (mv !== undefined || mpv !== undefined);
      vinculosWrap.hidden = !showVinculos;
      if (showVinculos) {
        vinculosWrap.querySelector<HTMLElement>('[data-vcount="1"]')!.textContent = mv !== undefined ? String(mv) : '0';
        vinculosWrap.querySelector<HTMLElement>('[data-vcount="2"]')!.textContent = mpv !== undefined ? String(mpv) : '0';
      }
      resetVinculos();
    } catch (e) {
      articleEl.innerHTML = `<div class="document-error">Error al mostrar la norma: ${String(e)}</div>`;
    }
  });

  store.loadingNorma.subscribe((loading) => {
    articleEl.classList.toggle('loading', loading);
    if (loading) {
      titleEl.textContent = 'Cargando norma...';
      subEl.textContent = '';
      articleEl.innerHTML = '<div class="document-loading"><div class="spinner"></div><br/><br/>Buscando documento...</div>';
    }
  });

  store.normaError.subscribe((err) => {
    if (err) articleEl.innerHTML = `<div class="document-error">No se pudo cargar la norma: ${err}</div>`;
  });

  if (substatsIntervalId === null) {
    substatsIntervalId = setInterval(() => {
      const norma = store.currentNorma.get();
      if (norma) updateSubStatus(subStatus, norma);
    }, 60_000);
  }

  // --- Inicialización y Wiring del Asistente IA & Scroll Progress ---
  aiPanel = root.querySelector<HTMLElement>('[data-ai-panel]')!;
  aiChatHistory = root.querySelector<HTMLElement>('[data-ai-chat-history]')!;
  aiInput = root.querySelector<HTMLTextAreaElement>('.viewer-ai-input')!;
  selectionPopover = root.querySelector<HTMLElement>('[data-selection-popover]')!;
  scrollProgressBar = root.querySelector<HTMLElement>('[data-scroll-progress]')!;

  // Cerrar panel de IA
  aiPanel.querySelector('[data-ai-close]')?.addEventListener('click', () => {
    aiPanel.classList.add('hidden');
  });

  // Cargar ancho guardado para el Asistente de IA
  const savedAiWidth = loadAiWidth();
  if (savedAiWidth) {
    const max = Math.max(AI_MIN_WIDTH + 80, Math.floor(viewerBody.clientWidth * 0.65));
    setAiWidth(clamp(savedAiWidth, AI_MIN_WIDTH, max));
  }

  // Resizer del panel de IA
  const aiResizer = root.querySelector<HTMLElement>('[data-ai-resizer]')!;
  aiResizer.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    aiResizer.setPointerCapture(e.pointerId);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    
    const rect = viewerBody.getBoundingClientRect();
    const aiRect = aiPanel.getBoundingClientRect();
    const rightEdge = aiRect.right;
    
    const onMove = (ev: PointerEvent): void => {
      const desired = rightEdge - ev.clientX;
      const max = Math.max(AI_MIN_WIDTH + 80, Math.floor(rect.width * 0.65));
      const next = clamp(desired, AI_MIN_WIDTH, max);
      setAiWidth(next);
      saveAiWidth(next);
    };
    const onUp = (ev: PointerEvent): void => {
      aiResizer.releasePointerCapture(ev.pointerId);
      aiResizer.removeEventListener('pointermove', onMove);
      aiResizer.removeEventListener('pointerup', onUp);
      aiResizer.removeEventListener('pointercancel', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    aiResizer.addEventListener('pointermove', onMove);
    aiResizer.addEventListener('pointerup', onUp);
    aiResizer.addEventListener('pointercancel', onUp);
  });

  // Toggling panel de IA desde la toolbar
  root.querySelector('[data-action="ai-chat"]')?.addEventListener('click', () => {
    aiPanel.classList.toggle('hidden');
    if (!aiPanel.classList.contains('hidden')) {
      aiInput.focus();
      navPanel.classList.add('hidden');
    }
  });

  // Acciones rápidas de IA en el panel
  aiPanel.querySelector('[data-ai-action="summary"]')?.addEventListener('click', () => {
    const text = articleEl.innerText || '';
    if (text.trim()) {
      void askAI('summary', text.slice(0, 12000));
    }
  });

  aiPanel.querySelector('[data-ai-action="obligations"]')?.addEventListener('click', () => {
    const text = articleEl.innerText || '';
    if (text.trim()) {
      void askAI('obligations', text.slice(0, 12000));
    }
  });

  // Chat input textarea auto-resize y envío
  const sendInput = () => {
    const queryText = aiInput.value.trim();
    if (!queryText) return;
    aiInput.value = '';
    aiInput.style.height = '38px';
    void askAI('chat', queryText);
  };

  aiInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendInput();
    }
  });

  aiInput.addEventListener('input', () => {
    aiInput.style.height = 'auto';
    aiInput.style.height = `${Math.min(aiInput.scrollHeight, 120)}px`;
  });

  aiPanel.querySelector('[data-ai-send]')?.addEventListener('click', sendInput);

  // Popover contextual de selección
  selectionPopover.querySelector('[data-selection-ai-action="explain"]')?.addEventListener('click', () => {
    const sel = window.getSelection();
    const text = sel ? sel.toString().trim() : '';
    if (text) {
      void askAI('explain', text);
    }
  });

  selectionPopover.querySelectorAll('[data-hl-color]').forEach(btn => {
    btn.addEventListener('click', () => {
      const color = btn.getAttribute('data-hl-color')!;
      highlightSelection(color);
    });
  });

  selectionPopover.querySelector('[data-selection-action="note"]')?.addEventListener('click', () => {
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && articleEl.contains(sel.anchorNode)) {
      pendingRange = sel.getRangeAt(0).cloneRange();
      
      // Limpiar selección para que no estorbe
      sel.removeAllRanges();
      selectionPopover.classList.add('hidden');
      
      showNoteModal('', (noteText) => {
        if (pendingRange) {
          highlightSelectionWithRange(pendingRange, 'yellow', noteText);
          pendingRange = null;
        }
      });
    }
  });

  // Manejo de clics en resaltados en el cuerpo del documento
  articleEl.addEventListener('click', (e) => {
    if (isDiffActive) return;

    const highlight = (e.target as HTMLElement).closest('.lx-highlight');
    if (highlight) {
      e.stopPropagation();
      const id = highlight.getAttribute('data-hl-id');
      if (id) {
        showHighlightTooltip(highlight as HTMLElement, id);
      }
    } else {
      removeActiveTooltip();
    }
  });

  // Evento pointerdown global para cerrar el popover si tocamos afuera
  document.addEventListener('pointerdown', (e) => {
    if (!selectionPopover.classList.contains('hidden') && !selectionPopover.contains(e.target as Node)) {
      selectionPopover.classList.add('hidden');
    }
  });

  // Evento selectionchange para mostrar el popover sobre la selección
  document.addEventListener('selectionchange', () => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !articleEl.contains(selection.anchorNode)) {
      selectionPopover.classList.add('hidden');
      return;
    }
    const text = selection.toString().trim();
    if (!text || text.length < 5) {
      selectionPopover.classList.add('hidden');
      return;
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const viewerBody = root.querySelector<HTMLElement>('[data-viewer-body]');
    if (viewerBody) {
      const bodyRect = viewerBody.getBoundingClientRect();
      
      // Mostrar temporalmente el popover de forma invisible para medirlo
      selectionPopover.style.visibility = 'hidden';
      selectionPopover.classList.remove('hidden');
      const popoverWidth = selectionPopover.offsetWidth || 140;
      const popoverHeight = selectionPopover.offsetHeight || 32;
      selectionPopover.style.visibility = '';

      const top = rect.top - bodyRect.top - popoverHeight - 4;
      const left = rect.left - bodyRect.left + (rect.width - popoverWidth) / 2;

      const maxLeft = bodyRect.width - popoverWidth - 8;
      selectionPopover.style.top = `${clamp(top, 8, bodyRect.height - 40)}px`;
      selectionPopover.style.left = `${clamp(left, 8, maxLeft)}px`;
    }
  });

  if (!globalListenersAttached) {
    globalListenersAttached = true;
  }
}

function paintNorma(norma: Norma): void {
  articleEl.classList.toggle('bcra-mode', norma.source === 'bcra');
  
  // Resetear estado de diff y tooltips al cambiar de norma
  isDiffActive = false;
  cachedOriginalNorma = null;
  const diffToggleBtn = rootEl.querySelector<HTMLElement>('[data-action="diff-toggle"]')!;
  if (diffToggleBtn) {
    diffToggleBtn.style.display = (norma.source === 'nacion') ? 'inline-flex' : 'none';
    diffToggleBtn.classList.remove('active');
  }
  removeActiveTooltip();

  const rawHtml = norma.source === 'bcra' ? norma.html : sanitizeHtml(norma.html);
  currentSanitized = normalizeDocumentStructure(rawHtml);

  const localKey = `annotations:html:${norma.source}:${norma.id}`;
  const savedHtml = localStorage.getItem(localKey);
  if (savedHtml) {
    articleEl.innerHTML = savedHtml;
  } else {
    articleEl.innerHTML = currentSanitized;
  }
  articleEl.querySelectorAll('table').forEach(table => {
    if (!table.parentElement?.classList.contains('table-responsive')) {
      const wrapper = document.createElement('div');
      wrapper.className = 'table-responsive';
      wrapper.style.overflowX = 'auto';
      wrapper.style.setProperty('-webkit-overflow-scrolling', 'touch');
      table.parentNode?.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  });
  currentAnchors = annotateArticles(articleEl);  // síncrono: anchors necesarios para nav inmediata
  clearFindHighlights();
  findInputEl.value = '';
  findCountEl.textContent = '0';
  updateArtSearch();
  updateDocContext();
  articleEl.scrollTop = 0;
  navPanel.classList.add('hidden');
  // Outline es visual — se difiere al idle para no bloquear el primer render del texto
  const scheduleOutline = typeof requestIdleCallback === 'function'
    ? (cb: () => void) => requestIdleCallback(cb, { timeout: 1000 })
    : (cb: () => void) => setTimeout(cb, 0);
  scheduleOutline(renderNavOutline);
}

function updateArtSearch(): void {
  artSearchWrap.classList.toggle('hidden', currentAnchors.length === 0);
  if (currentAnchors.length === 0) artSearchInput.value = '';
}

function navigateToArt(query: string, exact = false): void {
  let q = query.trim().toLowerCase();
  
  // Remover prefijos comunes como "articulo", "artículo", "art.", "art"
  q = q.replace(/^(art[íi]culo|art\.?|articulo)\s*/gi, '');
  
  // Remover puntos y caracteres no alfanuméricos al final
  q = q.replace(/[^0-9a-z]+$/gi, '');
  
  if (!q) return;
  
  const match = currentAnchors.find((a) => {
    const numStr = String(a.number).toLowerCase();
    if (exact) {
      return numStr === q;
    } else {
      return numStr === q || numStr.startsWith(q);
    }
  });
  if (match) {
    const target = articleEl.querySelector<HTMLElement>(`#${match.id}`);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function renderNavOutline(): void {
  const items: Array<{ id: string; label: string; kind: 'title' | 'subtitle' | 'article'; level: number }> = [];
  const seen = new Set<string>();
  articleEl.querySelectorAll<HTMLElement>('h1, h2, h3, h4').forEach((el) => {
    const text = (el.textContent ?? '').trim();
    if (!text) return;
    const level = Number(el.tagName.slice(1));
    const id = ensureNodeId(el, `sec-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`);
    if (seen.has(id)) return;
    seen.add(id);
    items.push({ id, label: text, kind: level <= 2 ? 'title' : 'subtitle', level });
  });
  currentAnchors.forEach((a) => {
    if (seen.has(a.id)) return;
    seen.add(a.id);
    items.push({ id: a.id, label: `Art. ${a.number} · ${a.text.slice(0, 90)}`, kind: 'article', level: 5 });
  });
  if (items.length === 0) {
    navList.innerHTML = '<p class="viewer-nav-empty">Sin secciones detectadas.</p>';
    return;
  }
  navList.innerHTML = items
    .map((item) => `<button class="viewer-nav-item" data-nav-id="${escHtml(item.id)}" data-kind="${item.kind}" data-level="${item.level}">${escHtml(item.label)}</button>`)
    .join('');
  navList.querySelectorAll<HTMLButtonElement>('[data-nav-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = articleEl.querySelector<HTMLElement>(`#${CSS.escape(btn.dataset.navId!)}`);
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
}

function ensureNodeId(el: HTMLElement, base: string): string {
  if (el.id) return el.id;
  let id = base || 'sec';
  let i = 1;
  while (document.getElementById(id)) { i += 1; id = `${base}-${i}`; }
  el.id = id;
  return id;
}

function clamp(n: number, min: number, max: number): number { return Math.max(min, Math.min(max, n)); }

function loadNavWidth(): number | null {
  const raw = localStorage.getItem(NAV_WIDTH_KEY);
  return raw ? Number(raw) : null;
}

function saveNavWidth(width: number): void {
  try { localStorage.setItem(NAV_WIDTH_KEY, String(Math.round(width))); } catch {}
}

function setNavWidth(width: number): void { navPanel.style.width = `${Math.round(width)}px`; }

function loadAiWidth(): number | null {
  const raw = localStorage.getItem(AI_WIDTH_KEY);
  return raw ? Number(raw) : null;
}

function saveAiWidth(width: number): void {
  try { localStorage.setItem(AI_WIDTH_KEY, String(Math.round(width))); } catch {}
}

function setAiWidth(width: number): void { aiPanel.style.width = `${Math.round(width)}px`; }

function updateDocContext(): void {
  const norma = store.currentNorma.get();
  if (!norma) { docContextEl.textContent = ''; return; }
  if (norma.source === 'bcra') { docContextEl.textContent = 'Documento PDF BCRA'; return; }
  const nodes = articleEl.querySelectorAll<HTMLElement>('h1, h2, h3, h4, .articulo');
  const threshold = articleEl.scrollTop + 28;
  let current: HTMLElement | undefined;
  for (const el of nodes) { if (el.offsetTop <= threshold) current = el; }
  if (!current) { docContextEl.textContent = ''; return; }
  const raw = (current.textContent ?? '').replace(/\s+/g, ' ').trim();
  const text = current.classList.contains('articulo')
    ? (() => {
      const m = raw.match(/Art(?:[íi]culo|\.)\s*(\d+)/i);
      return m ? `Artículo ${m[1]} · ${raw.slice(0, 100)}` : raw.slice(0, 110);
    })()
    : raw.slice(0, 110);
  docContextEl.textContent = text;
}

function clearFindHighlights(): void {
  articleEl.querySelectorAll<HTMLElement>('mark.doc-find-hit').forEach((mark) => {
    mark.replaceWith(document.createTextNode(mark.textContent ?? ''));
  });
  findHits = [];
  findHitIndex = -1;
  articleEl.normalize();
}

function applyFindHighlights(query: string, preventScroll = false): void {
  clearFindHighlights();
  if (!query || query.length < 2 || articleEl.classList.contains('bcra-mode')) { updateFindCount(); return; }
  const re = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
  const walker = document.createTreeWalker(articleEl, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let node = walker.nextNode();
  while (node) {
    const parent = node.parentElement;
    if (parent && !parent.closest('mark.doc-find-hit, .viewer-nav, .bcra-doc-head') && node.nodeValue?.trim()) {
      nodes.push(node as Text);
    }
    node = walker.nextNode();
  }
  for (const textNode of nodes) {
    const src = textNode.nodeValue ?? '';
    re.lastIndex = 0;
    if (!re.test(src)) continue;
    re.lastIndex = 0;
    let last = 0;
    const frag = document.createDocumentFragment();
    let m: RegExpExecArray | null;
    while ((m = re.exec(src)) !== null) {
      if (findHits.length >= 350) break;
      frag.appendChild(document.createTextNode(src.slice(last, m.index)));
      const mark = document.createElement('mark');
      mark.className = 'doc-find-hit';
      mark.textContent = m[0];
      frag.appendChild(mark);
      findHits.push(mark);
      last = m.index + m[0].length;
    }
    frag.appendChild(document.createTextNode(src.slice(last)));
    textNode.replaceWith(frag);
    if (findHits.length >= 350) break;
  }
  if (findHits.length > 0) {
    if (preventScroll) updateFindCount();
    else focusFindHit(1, true);
  } else {
    updateFindCount();
  }
}

function focusFindHit(step: 1 | -1, reset = false): void {
  if (findHits.length === 0) { updateFindCount(); return; }
  findHitIndex = reset ? (step > 0 ? 0 : findHits.length - 1) : (findHitIndex + step + findHits.length) % findHits.length;
  findHits.forEach((el, idx) => el.classList.toggle('active', idx === findHitIndex));
  updateFindCount();
  const active = findHits[findHitIndex];
  if (active) articleEl.scrollTo({ top: active.offsetTop - 150, behavior: 'smooth' });
}

function updateFindCount(): void {
  findCountEl.textContent = findHits.length > 0 ? `${findHitIndex + 1}/${findHits.length}` : '0';
}

async function doExportPdf(): Promise<void> {
  const norma = await getFreshNormaForExport();
  if (!norma) return;
  const { exportPdf } = await import('../../export/pdf');
  // Normalizamos el HTML antes de exportar para asegurar párrafos y saltos de línea
  const cleanHtml = normalizeDocumentStructure(norma.html);
  await exportPdf(norma, cleanHtml, currentAnchors);
}

async function doExportDocx(): Promise<void> {
  const norma = await getFreshNormaForExport();
  if (!norma) return;
  const { exportDocx } = await import('../../export/docx');
  const cleanHtml = normalizeDocumentStructure(norma.html);
  await exportDocx(norma, cleanHtml);
}

async function getFreshNormaForExport(): Promise<Norma | null> {
  const current = store.currentNorma.get();
  if (!current) return null;
  try {
    // Ya no activamos store.loadingNorma para evitar que la UI se borre durante la descarga
    const fresh = current.source === 'nacion' ? await api.getInfoleg(current.id, true) : current;
    return fresh;
  } catch (e) {
    store.normaError.set(`Error al verificar versión para exportar: ${String(e)}`);
    return null;
  }
}

function openTexact(): void {
  const norma = store.currentNorma.get();
  if (!norma) return;
  const url = norma.source === 'nacion' ? `https://servicios.infoleg.gob.ar/infolegInternet/verNorma.do?id=${encodeURIComponent(norma.id)}` : norma.source === 'bcra' ? norma.id : null;
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}

let toastTimer: any = null;
async function copyLink(toast: HTMLElement): Promise<void> {
  const norma = store.currentNorma.get();
  if (!norma) return;
  const hash = norma.source === 'nacion' ? `#/n/${norma.id}` : norma.source === 'cordoba' ? `#/c/${norma.ori ?? 'L'}/${norma.id}` : `#/b/${encodeURIComponent(norma.id)}`;
  await navigator.clipboard.writeText(`${location.origin}${location.pathname}${hash}`);
  toast.classList.add('show');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('show'), 2200);
}

async function copyAllText(toast: HTMLElement): Promise<void> {
  await navigator.clipboard.writeText(articleEl.innerText || '');
  toast.classList.add('show');
  setTimeout(() => toast.classList.remove('show'), 2200);
}

async function doExportTxt(): Promise<void> {
  const norma = store.currentNorma.get();
  if (!norma) return;
  
  // Extraemos el texto limpio. Si es de InfoLeg, intentamos limpiar etiquetas si el DOM está cargando
  const text = articleEl.innerText.includes('Buscando documento...') 
    ? norma.html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() 
    : articleEl.innerText;

  const blob = new Blob([text || ''], { type: 'text/plain;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${norma.meta.tipo}_${norma.meta.numero}.txt`.replace(/[^\w.-]/g, '_');
  a.click();
}

function labelForEstado(estado: string): string {
  if (estado === 'vigente') return '✓ Vigente';
  if (estado === 'derogada') return '⨯ Derogada';
  if (estado === 'modificada') return '⚠ Modificada';
  return '';
}

function updateSubStatus(el: HTMLElement, norma: Norma): void {
  el.textContent = `Última verificación: ${new Date(norma.fetchedAt).toLocaleTimeString('es-AR')} · ${relativeTime(norma.fetchedAt)}`;
}

function resetVinculos(): void {
  activeVModo = null;
  vinculosList.hidden = true;
  vinculosWrap.querySelectorAll<HTMLButtonElement>('[data-vbtn]').forEach(b => b.classList.remove('active'));
}

async function handleVinculosClick(btn: HTMLButtonElement): Promise<void> {
  const modo = Number(btn.dataset.vbtn) as 1 | 2;
  const norma = store.currentNorma.get();
  if (!norma) return;
  if (activeVModo === modo) { resetVinculos(); return; }
  activeVModo = modo;
  vinculosWrap.querySelectorAll<HTMLButtonElement>('[data-vbtn]').forEach(b => b.classList.remove('active'));
  btn.classList.add('active');
  await loadVinculos(norma.id, modo);
}

async function loadVinculos(id: string, modo: 1 | 2): Promise<void> {
  vinculosList.hidden = false;
  vinculosList.innerHTML = '<p class="vinculos-loading">Cargando relaciones…</p>';
  try {
    vinculosItems = await api.getInfolegVinculos(id, modo);
    renderVinculosContent();
  } catch (e) {
    vinculosList.innerHTML = `<p class="vinculos-empty">Error al cargar relaciones</p>`;
  }
}

function renderVinculosContent(): void {
  const filtered = vinculosItems.filter((h) => !vinculosTipoFilter || h.tipo === vinculosTipoFilter);
  vinculosList.innerHTML = `
    <div class="vinculos-items">
      ${filtered.length === 0 ? '<p class="vinculos-empty">Sin resultados.</p>' : filtered.map((h) =>
        `<button class="vinculo-item" data-vid="${escHtml(h.id)}">
          <span class="vi-meta">${escHtml(h.tipo)} Nº ${escHtml(h.numero)}</span>
          <span class="vi-title">${escHtml(h.titulo ?? '')}</span>
        </button>`
      ).join('')}
    </div>
  `;
  vinculosList.querySelectorAll<HTMLButtonElement>('[data-vid]').forEach((btn) => {
    btn.addEventListener('click', () => void loadNorma('nacion', btn.dataset.vid!));
  });
}

function escHtml(s: string): string { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

function getWelcomeHtml(): string {
  const indexKey = 'annotations:index';
  let index: AnnotationIndexItem[] = [];
  try {
    const raw = localStorage.getItem(indexKey);
    if (raw) index = JSON.parse(raw);
  } catch {}

  let recentHtml = '';
  if (index.length > 0) {
    recentHtml = `
      <div class="recent-annotations-section" style="margin-top: 32px; width: 100%; max-width: 900px; padding: 0 16px; margin-left: auto; margin-right: auto; text-align: left;">
        <h2 style="font-size: 1.3em; font-family: var(--sans); font-weight: 700; color: var(--fg); margin-bottom: 16px; display: flex; align-items: center; gap: 8px;">
          <i class="ph-bold ph-folder-open" style="color: var(--primary);"></i> Espacio de Trabajo: Notas Recientes
        </h2>
        <div class="recent-annotations-list" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px;">
          ${index.map(item => `
            <div class="recent-annotation-card" data-load-recent-source="${item.source}" data-load-recent-id="${item.id}" data-load-recent-ori="${item.ori ?? ''}" style="
              background: var(--bg-glass);
              backdrop-filter: blur(12px) saturate(120%);
              -webkit-backdrop-filter: blur(12px) saturate(120%);
              border: 1px solid var(--border-glass);
              border-radius: var(--radius-md);
              padding: 16px;
              cursor: pointer;
              transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
              display: flex;
              flex-direction: column;
              gap: 8px;
            " onmouseover="this.style.transform='translateY(-2px)'; this.style.borderColor='var(--primary)'; this.style.boxShadow='var(--shadow-1)';" onmouseout="this.style.transform='none'; this.style.borderColor='var(--border-glass)'; this.style.boxShadow='none';">
              <div style="font-size: 11px; font-family: var(--sans); text-transform: uppercase; letter-spacing: 0.05em; color: var(--primary); font-weight: 700;">
                ${item.source === 'nacion' ? 'Nación · Infoleg' : 'Provincia · Córdoba'}
              </div>
              <div style="font-size: 13.5px; font-weight: 600; color: var(--fg); font-family: var(--sans); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.4;">
                ${item.title}
              </div>
              <div style="font-size: 11px; color: var(--fg-soft); font-family: var(--sans); text-align: right; margin-top: auto;">
                ${new Date(item.timestamp).toLocaleDateString()}
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  return `
    <div class="document-empty" style="max-width: 1000px; margin: 0 auto !important; padding: 0 0 60px;">
      <div class="welcome-bento">
        <!-- Tarjeta Principal de Cabecera -->
        <div class="bento-card bento-header">
          <h1>LexCore Argentina</h1>
          <p style="font-size: 15px; max-width: 600px; margin: 0 auto; color: var(--fg-muted);">
            Plataforma avanzada de lectura y análisis jurídico. Buscá leyes nacionales y provinciales con la velocidad de la luz.
          </p>
          <div style="margin-top: 12px; font-size: 13px; color: var(--fg-soft);">
            Presioná <kbd>Ctrl</kbd>+<kbd>K</kbd> en cualquier momento para buscar
          </div>
        </div>

        <!-- Tarjetas Bento Secundarias -->
        <div class="bento-card span-3">
          <h3><i class="ph ph-buildings" style="color: var(--primary);"></i> Búsqueda Federal</h3>
          <p>Leyes, decretos y resoluciones oficiales nacionales obtenidos en tiempo real de Infoleg.</p>
        </div>

        <div class="bento-card span-3">
          <h3><i class="ph ph-map-pin" style="color: var(--ok);"></i> Legislación de Córdoba</h3>
          <p>Acceso a toda la normativa provincial: leyes históricas y decretos actualizados de Córdoba.</p>
        </div>

        <div class="bento-card span-4">
          <h3><i class="ph ph-sparkle" style="color: var(--primary);"></i> Inteligencia Artificial</h3>
          <p>Explicá artículos técnicos de forma comprensible, extraé plazos clave y redactá resúmenes ejecutivos al instante con el asistente basado en Mistral AI.</p>
        </div>

        <div class="bento-card span-2">
          <h3><i class="ph ph-file-doc" style="color: var(--warn);"></i> Exportación</h3>
          <p>Descargá tus escritos con plantillas en formato Word (.docx) o PDF listos para presentar.</p>
        </div>
      </div>

      ${recentHtml}
    </div>
  `;
}

// ============================================================
// 🛠️ ESPACIO DE TRABAJO LOCAL & COMPARACIÓN DIFF DE REFORMAS
// ============================================================

interface AnnotationIndexItem {
  source: string;
  id: string;
  ori?: string;
  title: string;
  timestamp: number;
}

// 1. Persistencia de Anotaciones
function saveLocalAnnotations(): void {
  const norma = store.currentNorma.get();
  if (!norma) return;

  const localKey = `annotations:html:${norma.source}:${norma.id}`;
  localStorage.setItem(localKey, articleEl.innerHTML);

  const indexKey = 'annotations:index';
  let index: AnnotationIndexItem[] = [];
  try {
    const raw = localStorage.getItem(indexKey);
    if (raw) index = JSON.parse(raw);
  } catch {}

  index = index.filter(item => !(item.source === norma.source && item.id === norma.id));
  index.unshift({
    source: norma.source,
    id: norma.id,
    ori: norma.ori,
    title: norma.meta.titulo || norma.id,
    timestamp: Date.now()
  });

  if (index.length > 15) {
    const evicted = index.slice(15);
    index = index.slice(0, 15);
    evicted.forEach(item => {
      localStorage.removeItem(`annotations:html:${item.source}:${item.id}`);
    });
  }

  localStorage.setItem(indexKey, JSON.stringify(index));
}

function deleteAnnotation(id: string): void {
  const marks = articleEl.querySelectorAll(`mark[data-hl-id="${id}"]`);
  marks.forEach(mark => {
    const parent = mark.parentNode;
    if (parent) {
      while (mark.firstChild) {
        parent.insertBefore(mark.firstChild, mark);
      }
      parent.removeChild(mark);
    }
  });

  const norma = store.currentNorma.get();
  if (norma) {
    const remaining = articleEl.querySelector('mark[data-hl-id]');
    if (!remaining) {
      localStorage.removeItem(`annotations:html:${norma.source}:${norma.id}`);
      
      const indexKey = 'annotations:index';
      let index: AnnotationIndexItem[] = [];
      try {
        const raw = localStorage.getItem(indexKey);
        if (raw) index = JSON.parse(raw);
      } catch {}
      index = index.filter(item => !(item.source === norma.source && item.id === norma.id));
      localStorage.setItem(indexKey, JSON.stringify(index));
    } else {
      saveLocalAnnotations();
    }
  }
}

function updateAnnotationNote(id: string, note: string): void {
  const marks = articleEl.querySelectorAll(`mark[data-hl-id="${id}"]`);
  marks.forEach(mark => {
    if (note) {
      mark.setAttribute('data-note', note);
      mark.setAttribute('title', note);
    } else {
      mark.removeAttribute('data-note');
      mark.removeAttribute('title');
    }
  });
  saveLocalAnnotations();
}

// 2. Resaltado de Selección
function highlightSelection(color: string, note?: string): void {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || !articleEl.contains(selection.anchorNode)) {
    return;
  }
  const range = selection.getRangeAt(0);
  highlightSelectionWithRange(range, color, note);
  selection.removeAllRanges();
  selectionPopover.classList.add('hidden');
}

function highlightSelectionWithRange(range: Range, color: string, note?: string): void {
  const id = `hl-${Date.now()}`;
  const startContainer = range.startContainer;
  const endContainer = range.endContainer;

  if (startContainer === endContainer && startContainer.nodeType === Node.TEXT_NODE) {
    const textNode = startContainer as Text;
    const mark = document.createElement('mark');
    mark.className = `lx-highlight color-${color}`;
    mark.setAttribute('data-hl-id', id);
    if (note) {
      mark.setAttribute('data-note', note);
      mark.title = note;
    }
    textNode.splitText(range.endOffset);
    const middle = textNode.splitText(range.startOffset);
    middle.parentNode?.insertBefore(mark, middle);
    mark.appendChild(middle);
  } else {
    const textNodes: Text[] = [];
    const treeWalker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          if (range.intersectsNode(node)) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_REJECT;
        }
      }
    );

    let currentNode = treeWalker.nextNode();
    while (currentNode) {
      textNodes.push(currentNode as Text);
      currentNode = treeWalker.nextNode();
    }

    textNodes.forEach((textNode) => {
      let start = 0;
      let end = textNode.length;
      if (textNode === startContainer) {
        start = range.startOffset;
      }
      if (textNode === endContainer) {
        end = range.endOffset;
      }

      const mark = document.createElement('mark');
      mark.className = `lx-highlight color-${color}`;
      mark.setAttribute('data-hl-id', id);
      if (note) {
        mark.setAttribute('data-note', note);
        mark.title = note;
      }

      textNode.splitText(end);
      const middle = textNode.splitText(start);
      middle.parentNode?.insertBefore(mark, middle);
      mark.appendChild(middle);
    });
  }
  
  saveLocalAnnotations();
}

// 3. Modal de Nota
function showNoteModal(existingNote: string, onSave: (note: string) => void): void {
  document.querySelector('.lx-modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.className = 'lx-modal-overlay';
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    opacity: 0;
    transition: opacity 0.2s ease;
  `;

  overlay.innerHTML = `
    <div class="lx-modal-content" style="
      background: var(--bg-glass);
      backdrop-filter: blur(16px) saturate(120%);
      -webkit-backdrop-filter: blur(16px) saturate(120%);
      border: 1px solid var(--border-glass);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-glass);
      width: 90%;
      max-width: 440px;
      padding: 24px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      transform: scale(0.95);
      transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
    ">
      <h3 style="margin: 0; font-size: 1.2em; font-family: var(--sans); color: var(--primary); font-weight: 700; display: flex; align-items: center; gap: 8px;">
        <i class="ph-bold ph-pencil-simple-line"></i> Anotación Personal
      </h3>
      <textarea class="lx-note-textarea" placeholder="Escribe tu comentario o nota aquí..." style="
        width: 100%;
        min-height: 100px;
        padding: 12px;
        border-radius: var(--radius-md);
        border: 1px solid var(--border);
        background: var(--surface-container-lowest);
        color: var(--fg);
        font-family: var(--sans);
        font-size: 14px;
        resize: vertical;
        outline: none;
      ">${existingNote}</textarea>
      <div class="lx-modal-actions" style="display: flex; justify-content: flex-end; gap: 12px;">
        <button class="btn outlined sm" data-lx-modal-action="cancel" style="height: 36px; padding: 0 16px; font-weight: 500; cursor: pointer; border-radius: var(--radius-sm);">Cancelar</button>
        <button class="btn filled sm" data-lx-modal-action="save" style="height: 36px; padding: 0 16px; font-weight: 600; cursor: pointer; border-radius: var(--radius-sm); border: none; background: var(--primary); color: var(--on-primary);">Guardar</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  setTimeout(() => {
    overlay.style.opacity = '1';
    overlay.querySelector<HTMLElement>('.lx-modal-content')!.style.transform = 'scale(1)';
  }, 10);

  const textarea = overlay.querySelector('textarea')!;
  textarea.focus();
  textarea.setSelectionRange(textarea.value.length, textarea.value.length);

  const closeModal = () => {
    overlay.style.opacity = '0';
    overlay.querySelector<HTMLElement>('.lx-modal-content')!.style.transform = 'scale(0.95)';
    setTimeout(() => overlay.remove(), 200);
  };

  overlay.querySelector('[data-lx-modal-action="cancel"]')?.addEventListener('click', closeModal);
  overlay.querySelector('[data-lx-modal-action="save"]')?.addEventListener('click', () => {
    const val = textarea.value.trim();
    onSave(val);
    closeModal();
  });
}

// 4. Tooltip/Card de Highlight
function showHighlightTooltip(el: HTMLElement, id: string): void {
  if (activeTooltip && activeTooltip.getAttribute('data-for-id') === id) {
    return;
  }
  removeActiveTooltip();

  const note = el.getAttribute('data-note') || '';

  const tooltip = document.createElement('div');
  tooltip.className = 'lx-highlight-tooltip';
  tooltip.setAttribute('data-for-id', id);
  tooltip.style.cssText = `
    position: absolute;
    z-index: 95;
    background: var(--bg-glass);
    backdrop-filter: blur(12px) saturate(120%);
    -webkit-backdrop-filter: blur(12px) saturate(120%);
    border: 1px solid var(--border-glass);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-glass);
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 200px;
    max-width: 300px;
    animation: tooltipFadeIn 0.15s cubic-bezier(0, 0, 0.2, 1);
  `;

  const colorSelectorHtml = `
    <div style="display: flex; gap: 6px; align-items: center;">
      <button class="hl-color-btn yellow" data-change-color="yellow" style="width: 14px; height: 14px; border-radius: 50%; border: 1px solid rgba(0,0,0,0.15); cursor: pointer; background-color: #fef08a; padding: 0;"></button>
      <button class="hl-color-btn green" data-change-color="green" style="width: 14px; height: 14px; border-radius: 50%; border: 1px solid rgba(0,0,0,0.15); cursor: pointer; background-color: #bbf7d0; padding: 0;"></button>
      <button class="hl-color-btn pink" data-change-color="pink" style="width: 14px; height: 14px; border-radius: 50%; border: 1px solid rgba(0,0,0,0.15); cursor: pointer; background-color: #fbcfe8; padding: 0;"></button>
      <span style="flex-grow: 1;"></span>
      <button class="icon-btn" data-action="edit" title="Editar nota" style="background: transparent; border: none; cursor: pointer; display: flex; font-size: 14px; color: var(--fg); padding: 4px; border-radius: 4px;"><i class="ph ph-pencil-simple"></i></button>
      <button class="icon-btn" data-action="delete" title="Eliminar resaltado" style="background: transparent; border: none; cursor: pointer; display: flex; font-size: 14px; color: var(--bad); padding: 4px; border-radius: 4px;"><i class="ph ph-trash"></i></button>
    </div>
  `;

  tooltip.innerHTML = `
    <div style="font-size: 12.5px; line-height: 1.4; color: var(--fg); font-family: var(--sans); white-space: pre-wrap; max-height: 100px; overflow-y: auto; font-style: ${note ? 'normal' : 'italic'}">
      ${note ? note : 'Sin notas personales.'}
    </div>
    <div style="width: 100%; height: 1px; background: var(--border-glass);"></div>
    ${colorSelectorHtml}
  `;

  const viewerBody = rootEl.querySelector<HTMLElement>('[data-viewer-body]');
  if (viewerBody) {
    viewerBody.appendChild(tooltip);
    activeTooltip = tooltip;

    const bodyRect = viewerBody.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    
    const tooltipWidth = tooltip.offsetWidth || 220;
    const tooltipHeight = tooltip.offsetHeight || 60;
    
    const top = elRect.top - bodyRect.top - tooltipHeight - 8;
    const left = elRect.left - bodyRect.left + (elRect.width - tooltipWidth) / 2;
    
    tooltip.style.top = `${clamp(top, 8, bodyRect.height - tooltipHeight - 8)}px`;
    tooltip.style.left = `${clamp(left, 8, bodyRect.width - tooltipWidth - 8)}px`;

    tooltip.querySelectorAll('[data-change-color]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const color = btn.getAttribute('data-change-color')!;
        articleEl.querySelectorAll(`mark[data-hl-id="${id}"]`).forEach(m => {
          m.className = `lx-highlight color-${color}`;
        });
        saveLocalAnnotations();
        removeActiveTooltip();
      });
    });

    tooltip.querySelector('[data-action="edit"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      removeActiveTooltip();
      showNoteModal(note, (newNote) => {
        updateAnnotationNote(id, newNote);
      });
    });

    tooltip.querySelector('[data-action="delete"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      deleteAnnotation(id);
      removeActiveTooltip();
    });
  }
}

function removeActiveTooltip(): void {
  if (activeTooltip) {
    activeTooltip.remove();
    activeTooltip = null;
  }
}

// 5. Algoritmo de Diff a Nivel de Bloque y Palabra
function extractBlocks(html: string): { tag: string; id: string; text: string; html: string }[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blocks: { tag: string; id: string; text: string; html: string }[] = [];
  
  doc.body.querySelectorAll('p, li, table, h1, h2, h3, h4').forEach((el, index) => {
    if (el.parentElement && ['P', 'LI', 'H1', 'H2', 'H3', 'H4'].includes(el.parentElement.tagName)) {
      return;
    }
    blocks.push({
      tag: el.tagName.toLowerCase(),
      id: el.id || `block-${index}`,
      text: (el.textContent ?? '').trim(),
      html: el.outerHTML
    });
  });
  return blocks;
}

function cleanText(t: string): string {
  return t.replace(/\s+/g, ' ').trim().toLowerCase();
}

function getArticleIdFromText(text: string): string | null {
  const m = text.match(/^\s*Art(?:[íi]culo|\.)?\s*(\d+)\b/i);
  return m ? `art-${m[1]}` : null;
}

function isSimilarBlock(b1: { text: string }, b2: { text: string }): boolean {
  const art1 = getArticleIdFromText(b1.text);
  const art2 = getArticleIdFromText(b2.text);
  if (art1 && art2 && art1 === art2) return true;
  
  const words1 = new Set(b1.text.toLowerCase().split(/\W+/));
  const words2 = new Set(b2.text.toLowerCase().split(/\W+/));
  let intersection = 0;
  words1.forEach(w => { if (words2.has(w)) intersection++; });
  const union = words1.size + words2.size - intersection;
  return union > 0 && (intersection / union) > 0.25;
}

function diffWords(oldText: string, newText: string): string {
  const oldWords = oldText.split(/(\s+)/);
  const newWords = newText.split(/(\s+)/);
  
  const dp: number[][] = Array(oldWords.length + 1).fill(0).map(() => Array(newWords.length + 1).fill(0));
  
  for (let i = 1; i <= oldWords.length; i++) {
    for (let j = 1; j <= newWords.length; j++) {
      if (oldWords[i - 1] === newWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  
  let i = oldWords.length;
  let j = newWords.length;
  const result: string[] = [];
  
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      result.push(oldWords[i - 1]);
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      if (newWords[j - 1].trim()) {
        result.push(`<ins class="lx-diff-ins">${newWords[j - 1]}</ins>`);
      } else {
        result.push(newWords[j - 1]);
      }
      j--;
    } else {
      if (oldWords[i - 1].trim()) {
        result.push(`<del class="lx-diff-del">${oldWords[i - 1]}</del>`);
      } else {
        result.push(oldWords[i - 1]);
      }
      i--;
    }
  }
  
  return result.reverse().join('');
}

function diffDocuments(originalHtml: string, currentHtml: string): string {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = currentHtml;
  tempDiv.querySelectorAll('mark.lx-highlight').forEach(mark => {
    const parent = mark.parentNode;
    if (parent) {
      while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
      parent.removeChild(mark);
    }
  });
  const cleanCurrentHtml = tempDiv.innerHTML;

  const oldBlocks = extractBlocks(originalHtml);
  const newBlocks = extractBlocks(cleanCurrentHtml);
  
  const dp: number[][] = Array(oldBlocks.length + 1).fill(0).map(() => Array(newBlocks.length + 1).fill(0));
  for (let i = 1; i <= oldBlocks.length; i++) {
    for (let j = 1; j <= newBlocks.length; j++) {
      if (cleanText(oldBlocks[i - 1].text) === cleanText(newBlocks[j - 1].text)) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  
  let i = oldBlocks.length;
  let j = newBlocks.length;
  const segments: string[] = [];
  
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && cleanText(oldBlocks[i - 1].text) === cleanText(newBlocks[j - 1].text)) {
      segments.push(newBlocks[j - 1].html);
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      if (i > 0 && isSimilarBlock(oldBlocks[i - 1], newBlocks[j - 1])) {
        const diffHtml = diffWords(oldBlocks[i - 1].text, newBlocks[j - 1].text);
        const tag = newBlocks[j - 1].tag;
        segments.push(`<${tag} class="lx-diff-block-modified">${diffHtml}</${tag}>`);
        i--;
        j--;
      } else {
        const tag = newBlocks[j - 1].tag;
        segments.push(`<${tag} class="lx-diff-block-ins">${newBlocks[j - 1].text}</${tag}>`);
        j--;
      }
    } else {
      const tag = oldBlocks[i - 1].tag;
      segments.push(`<${tag} class="lx-diff-block-del">${oldBlocks[i - 1].text}</${tag}>`);
      i--;
    }
  }
  
  return `<div class="lx-diff-view">${segments.reverse().join('\n')}</div>`;
}

async function toggleDiff(): Promise<void> {
  const norma = store.currentNorma.get();
  if (!norma) return;

  const diffToggleBtn = rootEl.querySelector<HTMLElement>('[data-action="diff-toggle"]')!;
  
  if (isDiffActive) {
    isDiffActive = false;
    diffToggleBtn.classList.remove('active');
    
    const localKey = `annotations:html:${norma.source}:${norma.id}`;
    const savedHtml = localStorage.getItem(localKey);
    if (savedHtml) {
      articleEl.innerHTML = savedHtml;
    } else {
      articleEl.innerHTML = currentSanitized;
    }
    currentAnchors = annotateArticles(articleEl);
  } else {
    articleEl.classList.add('loading');
    
    try {
      if (!cachedOriginalNorma || cachedOriginalNorma.id !== norma.id) {
        cachedOriginalNorma = await api.getInfoleg(norma.id, false, true);
      }
      
      const currentHtml = currentSanitized; 
      const originalHtml = normalizeDocumentStructure(cachedOriginalNorma.html);
      
      const diffHtml = diffDocuments(originalHtml, currentHtml);
      
      articleEl.innerHTML = diffHtml;
      isDiffActive = true;
      diffToggleBtn.classList.add('active');
      
      currentAnchors = [];
    } catch (err) {
      alert(`Error al cargar la versión original: ${(err as Error).message}`);
    } finally {
      articleEl.classList.remove('loading');
    }
  }
}

async function askAI(action: 'summary' | 'explain' | 'chat' | 'obligations', text: string): Promise<void> {
  const norma = store.currentNorma.get();
  if (!norma) return;

  // Abrir panel si está cerrado
  aiPanel.classList.remove('hidden');
  if (window.innerWidth <= 1024) {
    navPanel.classList.add('hidden');
  }

  // Limpiar selección para ocultar el popover
  window.getSelection()?.removeAllRanges();
  selectionPopover.classList.add('hidden');

  // Agregar mensaje del usuario a la historia
  let userMsgText = text;
  if (action === 'explain') {
    userMsgText = `Explicar: "${text.length > 80 ? text.slice(0, 80) + '...' : text}"`;
  } else if (action === 'summary') {
    userMsgText = `Resumir esta norma`;
  } else if (action === 'obligations') {
    userMsgText = `Analizar plazos y obligaciones de esta norma`;
  }

  appendChatMessage('user', userMsgText);

  // Crear elemento de mensaje del asistente vacío/cargando
  const msgEl = appendChatMessage('assistant', 'Pensando...');
  
  try {
    const context = norma.meta.titulo || norma.id;
    let aiContext = context;
    
    // Si la acción es chat conceptual, o summary, usamos parte del texto
    if (action === 'chat' || action === 'summary' || action === 'obligations') {
      const docEl = document.createElement('div');
      docEl.innerHTML = norma.html;
      // Extraer solo texto legible y truncar a un tamaño seguro para no saturar tokens (15k chars)
      aiContext = `Título: ${context}\n\nContenido:\n${docEl.innerText.slice(0, 15000)}`;
    }

    const payload = {
      action: action === 'obligations' ? 'summary' : action,
      text: action === 'obligations' 
        ? 'Identificá y listá detalladamente todos los plazos, obligaciones y sanciones mencionados en el texto de esta norma.' 
        : text,
      context: aiContext,
      stream: true
    };

    const response = await fetch(`${BASE}/ia/analyze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Error desconocido' }));
      msgEl.innerHTML = `<span style="color: var(--error-color, #ef4444);">Error: ${err.error || response.statusText}</span>`;
      return;
    }

    msgEl.innerHTML = '';

    const reader = response.body?.getReader();
    if (!reader) {
      msgEl.textContent = 'Streaming no soportado en esta versión.';
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        
        if (trimmed.startsWith('data: ')) {
          const dataStr = trimmed.slice(6);
          if (dataStr === '[DONE]') continue;
          
          try {
            const parsed = JSON.parse(dataStr);
            const content = parsed.choices?.[0]?.delta?.content || '';
            if (content) {
              fullText += content;
              msgEl.textContent = fullText;
              aiChatHistory.scrollTop = aiChatHistory.scrollHeight;
            }
          } catch (e) {
            // Ignorar errores de formato en SSE stream
          }
        }
      }
    }
    
    // Al final, formateamos el markdown simple para que sea legible
    msgEl.innerHTML = renderMarkdownSimple(fullText);
    aiChatHistory.scrollTop = aiChatHistory.scrollHeight;
    
  } catch (err: any) {
    msgEl.innerHTML = `<span style="color: var(--error-color, #ef4444);">Error de red: ${err.message || String(err)}</span>`;
  }
}

function appendChatMessage(role: 'user' | 'assistant', text: string): HTMLElement {
  const msg = document.createElement('div');
  msg.className = `ai-msg ${role}`;
  
  if (role === 'user') {
    msg.style.cssText = `
      background: var(--primary);
      color: var(--on-primary);
      padding: 10px 14px;
      border-radius: var(--radius-md);
      font-size: 0.95em;
      line-height: 1.5;
      border-bottom-right-radius: var(--radius-xs);
      align-self: flex-end;
      max-width: 85%;
      border: 1px solid transparent;
      white-space: pre-wrap;
    `;
    msg.textContent = text;
    aiChatHistory.appendChild(msg);
    aiChatHistory.scrollTop = aiChatHistory.scrollHeight;
    return msg;
  } else {
    msg.style.cssText = `
      background: var(--surface-container-high);
      color: var(--on-surface);
      border-radius: var(--radius-md);
      font-size: 0.95em;
      line-height: 1.5;
      border-bottom-left-radius: var(--radius-xs);
      border: 1px solid var(--border);
      align-self: flex-start;
      max-width: 85%;
      position: relative;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
    `;

    const body = document.createElement('div');
    body.className = 'ai-msg-body';
    body.style.cssText = `
      padding: 10px 14.5px;
      padding-right: 4px;
      white-space: pre-wrap;
      flex: 1;
      box-sizing: border-box;
    `;
    body.innerHTML = text;
    msg.appendChild(body);

    const copyBtn = document.createElement('button');
    copyBtn.className = 'ai-msg-copy';
    copyBtn.title = 'Copiar respuesta';
    copyBtn.innerHTML = '<i class="ph ph-copy"></i>';
    copyBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const cleanText = body.innerText.trim();
      await navigator.clipboard.writeText(cleanText);
      
      copyBtn.innerHTML = '<i class="ph-bold ph-check" style="color: var(--primary);"></i>';
      setTimeout(() => {
        copyBtn.innerHTML = '<i class="ph ph-copy"></i>';
      }, 2000);
    });
    msg.appendChild(copyBtn);
    
    aiChatHistory.appendChild(msg);
    aiChatHistory.scrollTop = aiChatHistory.scrollHeight;
    return body;
  }
}

function renderMarkdownSimple(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\n/g, '<br>')
    .replace(/- (.*?)($|<br>)/g, '• $1$2');
}